import GoogleIcon from '@app/assets/icons/Google.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import EchoLogo from '@app/assets/simple-logo.svg?react';
import FullStarIcon from '@app/assets/icons/star-full.svg?react';
import EmptyStarIcon from '@app/assets/icons/star-empty.svg?react';
import { BusinessReviews } from '@app/models/reviews';
import { useState, useCallback, useRef, useMemo } from 'react';
import { Button } from '../Button/Button';
import { AIDropdown } from '../AIDropdown/AIDropdown';
import { useTranslation } from 'react-i18next';
import {
  fixGrammarAndSpelling,
  improveText,
  makeTextConcise,
  makeTextFormal,
  makeTextLonger,
  regenerateText,
} from '@app/services/service';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import ToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import GrammerIcon from '@app/assets/icons/ai/grammar.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import LongerIcon from '@app/assets/icons/ai/text-longer.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';

export const PreviewReviewBox = ({
  review,
  onReviewUpdate,
  onClose,
  thumbnails,
  onThumbnailsUpdate,
  accessToken,
  isBasicPlan = false,
}: {
  review: BusinessReviews;
  onReviewUpdate?: (updatedReview: string) => void;
  onClose?: () => void;
  thumbnails: string[];
  onThumbnailsUpdate?: (updatedThumbnails: string[]) => void;
  accessToken?: string;
  isBasicPlan?: boolean;
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedReview, setEditedReview] = useState(review.review);
  const [originalPreviewValue, setOriginalPreviewValue] = useState(
    review.review
  );
  const [hasStoppedTyping, setHasStoppedTyping] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mapping for AI action labels to translation keys
  const aiActionKeyMap: Record<string, string> = {
    'Fix grammar & spelling': 'fixgrammarandspelling',
    'Fix grammar and spelling': 'fixgrammarandspelling',
    'Make it concise': 'makeitconcise',
    'Make it more concise': 'makeitmoreconcise',
    'Make it longer': 'makeitlonger',
    'Structure Help': 'structurehelp',
    Formalize: 'formalize',
    'Improve text': 'improvetext',
    'Make it more formal': 'makeitmoreformal',
    Regenerate: 'regenerate',
  };

  const handleTypingPause = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setHasStoppedTyping(false);

    typingTimeoutRef.current = setTimeout(() => {
      setHasStoppedTyping(true);
    }, 2000);
  }, []);

  const handleDecline = () => {
    setEditedReview(originalPreviewValue);
    if (textareaRef.current) {
      textareaRef.current.value = originalPreviewValue;
    }
    setSuggestion(null);
  };

  const handleSave = () => {
    if (onReviewUpdate) {
      onReviewUpdate(editedReview);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedReview(review.review);
    if (textareaRef.current) {
      textareaRef.current.value = review.review;
    }
    setIsEditing(false);
  };

  const handleRemoveImage = (index: number) => {
    if (onThumbnailsUpdate) {
      const newThumbnails = [...thumbnails];
      URL.revokeObjectURL(newThumbnails[index]);
      newThumbnails.splice(index, 1);
      onThumbnailsUpdate(newThumbnails);
    }
  };

  const actionsFull = useMemo(
    () => [
      {
        label: 'Fix grammar and spelling',
        Icon: <GrammerIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await fixGrammarAndSpelling(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
      {
        label: 'Make it more concise',
        Icon: <ConciseIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await makeTextConcise(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
      {
        label: 'Make it longer',
        Icon: <LongerIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await makeTextLonger(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
      {
        label: 'Structure Help',
        Icon: <FormatIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await improveText(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
      {
        label: 'Make it more formal',
        Icon: <ToneIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await makeTextFormal(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
      {
        label: 'Regenerate',
        Icon: <RegenerateIcon className="w-4 h-4" />,
        onClick: async () => {
          if (textareaRef.current) {
            const result = await regenerateText(
              textareaRef.current.value,
              accessToken
            );
            if (result?.data) {
              setSuggestion(result.data.text);
              setOriginalPreviewValue(textareaRef.current.value);
              textareaRef.current.value = result.data.text;
              setEditedReview(result.data.text);
            }
          }
        },
      },
    ],
    [accessToken]
  );

  // For icon rendering, safely get platform
  const platform = (review as any).platform ?? 'internal';

  return (
    <div className="bg-white rounded-2xl p-3 sm:p-4 space-y-3 sm:space-y-4">
      <div className="flex justify-between items-center gap-2 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-base-seasalt rounded-full flex items-center justify-center">
          {platform === 'yelp' ? (
            <YelpIcon width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
          ) : platform === 'internal' ? (
            <EchoLogo width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
          ) : (
            <GoogleIcon width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
          )}
        </div>
        <div className="mr-auto space-y-1">
          <h4 className="font-semibold text-xs sm:text-sm">
            {review.firstName} {review.lastName}
            <span className="bg-system-successLight text-system-success p-1 text-[10px] sm:text-xs rounded-sm ml-1 sm:ml-2">
              {t('leaveReview.new')}
            </span>
          </h4>

          <p className="text-[10px] sm:text-xs text-base-coolGray">
            {t('leaveReview.leftReviewJustNow')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 sm:gap-2">
          <button
            onClick={onClose}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-base-ghostWhite transition-colors"
            aria-label={t('leaveReview.closePreview')}
          >
            <svg
              width="14"
              height="14"
              className="sm:w-4 sm:h-4"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {!isEditing && (
            <Button
              kind="secondary"
              onClick={() => setIsEditing(!isEditing)}
              className="min-w-fit text-xs sm:text-sm py-1 sm:py-2"
            >
              {t('leaveReview.edit')}
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) =>
          index < review.rating ? (
            <FullStarIcon
              key={index}
              width={14}
              height={14}
              className="sm:w-4 sm:h-4"
            />
          ) : (
            <EmptyStarIcon
              key={index}
              width={14}
              height={14}
              className="sm:w-4 sm:h-4"
            />
          )
        )}
      </div>
      <div>
        {isEditing ? (
          <div className="space-y-2">
            <div className="flex flex-row justify-end items-start gap-2 sm:gap-4 mb-2">
              {editedReview && !isBasicPlan && (
                <AIDropdown
                  key="full"
                  actions={actionsFull.map((action) => ({
                    ...action,
                    label: t(
                      `leaveReview.aiAssistance.${aiActionKeyMap[action.label] || action.label}`
                    ),
                  }))}
                  showOnClick={hasStoppedTyping}
                />
              )}
            </div>
            <textarea
              ref={textareaRef}
              name="preview-review"
              value={editedReview}
              onChange={(e) => {
                setEditedReview(e.target.value);
                handleTypingPause();
              }}
              className="w-full p-2 text-sm sm:text-base border border-base-ghostWhite rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              rows={4}
            />
            {suggestion && (
              <div className="flex justify-end space-x-2 sm:space-x-4 mt-2">
                <Button
                  kind="secondary"
                  type="button"
                  className="w-24 sm:w-28 text-xs py-1"
                  onClick={handleDecline}
                >
                  {t('leaveReview.aiAssistance.decline')}
                </Button>
              </div>
            )}
            <div className="flex flex-col sm:flex-row justify-center gap-2 mt-4">
              <Button
                kind="secondary"
                onClick={handleCancel}
                className="w-full sm:w-auto text-xs sm:text-sm py-2"
              >
                {t('leaveReview.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                className="w-full sm:w-auto text-xs sm:text-sm py-2"
              >
                {t('leaveReview.applyChanges')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs sm:text-sm">{editedReview}</p>
        )}
      </div>
      {thumbnails && thumbnails.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {thumbnails.map((thumb, index) => (
            <div
              key={index}
              className="w-full aspect-square relative rounded-[4px] overflow-hidden"
            >
              <img
                src={thumb}
                className="absolute w-full h-full object-cover object-center"
                alt={t('leaveReview.photoAlt', 'Review photo')}
              />
              <div className="bg-black/30 inset-0 absolute" />
              {isEditing && (
                <button
                  onClick={() => handleRemoveImage(index)}
                  type="button"
                  className="h-6 w-6 bg-white/50 flex items-center justify-center top-0 right-0 absolute z-10"
                  aria-label={t('leaveReview.removePhoto', 'Remove photo')}
                >
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0"
                  >
                    <path
                      d="M6.5 1.5L1.5 6.5M6.5 6.5L1.5 1.5"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
